/**
 * PROVINCIA, CANTÓN Y DISTRITO, en cascada.
 *
 * POR QUÉ EXISTE. Esta misma cascada estaba escrita a mano dos veces —en el
 * registro y en el perfil— con el mismo reseteo, los mismos `disabled` y los
 * mismos textos. Dos copias de una regla son dos lugares donde arreglarla, y
 * la tercera pantalla que la necesitaba (las canchas) iba a ser la tercera
 * copia. Es literalmente el mismo componente: lo único que cambia es el
 * prefijo de los `id`, que tienen que ser únicos en la página.
 *
 * EL RESETEO EN CASCADA ES LA RAZÓN DE SER DE ESTO. Cambiar de provincia
 * limpia el cantón Y el distrito; cambiar de cantón limpia el distrito. Un
 * distrito de San José colgando de Guanacaste no es un dato feo: es un dato
 * IMPOSIBLE, y el servidor lo rechaza. Sin el reseteo, la pantalla deja armar
 * esa combinación y el error aparece recién al guardar.
 */
import { useTranslation } from 'react-i18next';
import { PROVINCES, cantonsOfProvince, districtsOfCanton } from '@tennisup/costa-rica-geo';

import { Field } from './Field';
import { Select } from './Select';

export interface GeoValue {
  provinceCode: string;
  cantonCode: string;
  districtCode: string;
}

export function GeoPicker({
  idPrefix,
  value,
  onChange,
  fieldErrors = {},
  /** El distrito no siempre hace falta: para filtrar una lista alcanza el cantón. */
  withDistrict = true,
}: {
  idPrefix: string;
  value: GeoValue;
  onChange: (value: GeoValue) => void;
  fieldErrors?: Record<string, string>;
  withDistrict?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  const cantons = value.provinceCode === '' ? [] : cantonsOfProvince(value.provinceCode);
  const districts = value.cantonCode === '' ? [] : districtsOfCanton(value.cantonCode);

  const vacío = { value: '', label: t('common.selectPlaceholder') };

  return (
    <>
      <Field
        id={`${idPrefix}-province`}
        label={t('register.province')}
        error={fieldErrors['provinceCode']}
      >
        {(control) => (
          <Select
            {...control}
            value={value.provinceCode}
            options={[vacío, ...PROVINCES.map((p) => ({ value: p.code, label: p.name }))]}
            onChange={(provinceCode) =>
              onChange({ provinceCode, cantonCode: '', districtCode: '' })
            }
          />
        )}
      </Field>

      <div className="row">
        <Field
          id={`${idPrefix}-canton`}
          label={t('register.canton')}
          error={fieldErrors['cantonCode']}
        >
          {(control) => (
            <Select
              {...control}
              value={value.cantonCode}
              disabled={value.provinceCode === ''}
              options={[vacío, ...cantons.map((c) => ({ value: c.code, label: c.name }))]}
              onChange={(cantonCode) => onChange({ ...value, cantonCode, districtCode: '' })}
            />
          )}
        </Field>

        {withDistrict && (
          <Field
            id={`${idPrefix}-district`}
            label={t('register.district')}
            error={fieldErrors['districtCode']}
          >
            {(control) => (
              <Select
                {...control}
                value={value.districtCode}
                disabled={value.cantonCode === ''}
                options={[vacío, ...districts.map((d) => ({ value: d.code, label: d.name }))]}
                onChange={(districtCode) => onChange({ ...value, districtCode })}
              />
            )}
          </Field>
        )}
      </div>
    </>
  );
}
